import * as React from 'react';
import type { Highlight, PositionedHighlight } from '../types';
import { isOwnHighlight } from '../helpers/isOwnHighlight';
import { noteColorForHighlight } from '../helpers/noteColor';
import { typeIcon } from '../helpers/formatting';
import styles from '../PdfRenderer.module.scss';

function noteLabel(h: Highlight): number | null {
    if (h.detail?.noteId != null) return h.detail.noteId;
    if (/^\d+-\d+$/.test(h.id)) return parseInt(h.id, 10);
    return null;
}

interface PositionedMarkersProps {
    highlights: Highlight[];
    pageNumber: number;
    dimension: { width: number; height: number };
    moveMode: boolean;
    moveDragRef: React.MutableRefObject<any>;
    pdfLayersRef: React.RefObject<HTMLDivElement | null>;
    onPopupEnter: (h: Highlight) => void;
    onScheduleClose: () => void;
    onOpenEditModal: (h: Highlight) => void;
    onMoveBlocked?: () => void;
    canMove?: (h: Highlight) => boolean;
}

export function PositionedMarkers({
    highlights,
    pageNumber,
    dimension,
    moveMode,
    moveDragRef,
    pdfLayersRef,
    onPopupEnter,
    onScheduleClose,
    onOpenEditModal,
    onMoveBlocked,
    canMove,
}: PositionedMarkersProps) {
    const { width: dw, height: dh } = dimension;

    return (
        <>
            {highlights
                .filter((h): h is PositionedHighlight =>
                    h.pageNumber === pageNumber && h.type === 'positioned'
                )
                .map((h) => {
                    const lbl = noteLabel(h);
                    const c = noteColorForHighlight(isOwnHighlight(h), h.persistStatus);
                    const movable = canMove?.(h) ?? isOwnHighlight(h);
                    return (
                        <React.Fragment key={h.id}>
                            {/* Одна пилюля вместо «точка + подпись рядом». Позиция
                                по-прежнему задаётся left/top в px с центрированием
                                через transform — useMoveDrag читает их обратно
                                как parseFloat(style.left)/width, эта математика
                                не меняется. */}
                            <div
                                data-marker-id={h.id}
                                className={styles.marker}
                                style={{
                                    left: `${h.x * dw}px`,
                                    top: `${h.y * dh}px`,
                                    background: c.stroke,
                                    cursor: moveMode && movable ? 'move' : undefined
                                }}
                                onMouseDown={(e) => {
                                    if (!moveMode) return;
                                    if (!movable) { onMoveBlocked?.(); return; }
                                    e.preventDefault();
                                    e.stopPropagation();
                                    moveDragRef.current = {
                                        id: h.id,
                                        el: e.currentTarget,
                                        type: 'positioned',
                                        startNX: (e.clientX - pdfLayersRef.current!.getBoundingClientRect().left) / dw,
                                        startNY: (e.clientY - pdfLayersRef.current!.getBoundingClientRect().top) / dh,
                                        startLeft: h.x * dw,
                                        startTop: h.y * dh,
                                    };
                                    document.body.style.cursor = 'grabbing';
                                }}
                                onMouseEnter={() => {
                                    if (moveMode) return;
                                    onPopupEnter(h);
                                }}
                                onMouseLeave={moveMode ? undefined : onScheduleClose}
                                onClick={(e) => {
                                    if (moveMode) return;
                                    e.stopPropagation();
                                    onOpenEditModal(h);
                                }}
                            >
                                <i className={typeIcon(h)} />
                                {lbl != null && lbl}
                            </div>
                        </React.Fragment>
                    );
                })}
        </>
    );
}
